import React, { useState } from 'react';
import { Box, Card, CardContent, Typography, Button, Alert, LinearProgress, FormControlLabel, Switch, Paper } from '@mui/material';
import { Download, CloudUpload, TableChart } from '@mui/icons-material';
import { useInvestmentsImportExport } from '../../hooks/useInvestmentsImportExport';
import PageHeader from '../../components/layout/PageHeader';
import AutoGrid from '../../components/layout/AutoGrid';

const InvestmentsImportExportPage: React.FC = () => {
  const { loading, error, exportAll, importAll, downloadTemplate, downloadFile, clearError } = useInvestmentsImportExport();
  const [file, setFile] = useState<File | null>(null);
  const [clearExisting, setClearExisting] = useState(false);
  const [result, setResult] = useState<{
    success: boolean;
    message: string;
    details?: string[];
  } | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setResult(null);
      clearError();
    }
  };

  const onExport = async () => {
    clearError();
    const blob = await exportAll();
    if (blob) downloadFile(blob, `investments_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  const onTemplate = async () => {
    clearError();
    const blob = await downloadTemplate();
    if (blob) downloadFile(blob, 'investments_template.xlsx');
  };

  const onImport = async () => {
    if (!file) return;
    clearError();
    const res = await importAll(file, clearExisting);
    if (res) {
      setResult({
        success: res.success,
        message: res.message,
        details: [...(res.errors || []), ...(res.warnings || [])],
      });
    }
  };

  return (
    <Box>
      <PageHeader title="Importar / Exportar investimentos" />

      <AutoGrid min={360}>
        {/* Import */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Importar investimentos</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Carregue o ficheiro XLSX com as folhas Stocks, Purchases, Dividends e Sales. Use o modelo para obter o formato correto.
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button size="small" startIcon={<TableChart />} onClick={onTemplate}>Descarregar modelo</Button>
            </Box>

            <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
              <Button component="label" startIcon={<CloudUpload />}>Selecionar ficheiro XLSX
                <input hidden type="file" accept=".xlsx" onChange={handleFileChange} />
              </Button>
              {file && (
                <Typography variant="body2" sx={{ mt: 1 }}>Selecionado: {file.name}</Typography>
              )}
              <FormControlLabel sx={{ display: 'block', mt: 1 }}
                control={<Switch checked={clearExisting} onChange={(e) => setClearExisting(e.target.checked)} />}
                label="Apagar os investimentos existentes antes de importar" />
            </Paper>

            {loading && <LinearProgress sx={{ mb: 2 }} />}
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {result && (
              <Alert severity={result.success ? 'success' : 'warning'} sx={{ mb: 2 }}>
                <Typography variant="subtitle2">{result.message}</Typography>
                {result.details && result.details.length > 0 && (
                  <ul style={{ margin: '4px 0 0 16px' }}>
                    {result.details.map((d, i) => <li key={i}>{d}</li>)}
                  </ul>
                )}
              </Alert>
            )}

            <Button variant="contained" onClick={onImport} disabled={!file || loading}>Importar</Button>
          </CardContent>
        </Card>

        {/* Export */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Exportar investimentos</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Descarregue um ficheiro XLSX com todas as suas ações, compras, dividendos e vendas ativos.
            </Typography>
            {loading && <LinearProgress sx={{ mb: 2 }} />}
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Button variant="contained" startIcon={<Download />} onClick={onExport} disabled={loading}>Exportar tudo</Button>
          </CardContent>
        </Card>
      </AutoGrid>
    </Box>
  );
};

export default InvestmentsImportExportPage;
